/**
 * Auszahlungslauf als Bankdatei: SEPA-Überweisung (pain.001.001.09, SEPA-Regelwerk seit November 2023) und CSV für
 * Banken ohne SEPA-Upload oder für Nicht-EUR-Auszahlungen. Rein; die Oberfläche lädt die Datei herunter, die Datenbank
 * hält den Lauf mit eingefrorenen Bankdaten fest.
 */
import { csvFormulaGuard } from "./csv.js";
import { isValidIban } from "./document-model.js";
import { isQrIban, isSwissIban } from "./swiss-qr-bill.js";
import type { Cents } from "./types.js";

export interface SepaParty {
  name: string;
  iban: string;
  bic?: string | null;
}

export interface SepaPayment extends SepaParty {
  /** Eindeutig je Zahlung, max. 35 Zeichen (z. B. Rechnungsnummer + Lauf) */
  endToEndId: string;
  amountCents: Cents;
  /** Verwendungszweck (unstrukturiert), max. 140 Zeichen */
  reference: string;
}

export interface SepaTransfer {
  /** Nachrichten-ID, max. 35 Zeichen, eindeutig je Datei */
  messageId: string;
  /** ISO-Zeitstempel der Erstellung */
  createdAt: string;
  /** gewünschtes Ausführungsdatum YYYY-MM-DD */
  executionDate: string;
  debtor: SepaParty;
  payments: readonly SepaPayment[];
  /** Sammelbuchung auf dem Konto der Agentur (eine Belastung) oder Einzelbuchungen */
  batchBooking?: boolean;
}

export interface SepaProblem {
  code: "DEBTOR_IBAN" | "DEBTOR_NAME" | "NO_PAYMENTS" | "IBAN" | "NAME" | "AMOUNT" | "BIC" | "DATE" | "ID";
  index: number | null;
}

/** Zeichensatz des SEPA-Regelwerks (lateinische Grundzeichen); Umlaute werden umschrieben, alles andere wird zum Leerzeichen. */
export function sepaText(value: string, max: number): string {
  const map: Record<string, string> = { ä: "ae", ö: "oe", ü: "ue", Ä: "Ae", Ö: "Oe", Ü: "Ue", ß: "ss", "&": "+", "–": "-", "—": "-", "„": "", "“": "", "”": "", "’": "'" };
  const replaced = [...value.normalize("NFC")].map((c) => map[c] ?? c).join("");
  const clean = replaced.normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^A-Za-z0-9/?:().,'+\- ]/g, " ").replace(/\s+/g, " ").trim();
  return clean.slice(0, max);
}

const esc = (s: string) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const ibanOf = (s: string) => s.replace(/\s+/g, "").toUpperCase();
const amount = (c: Cents) => (c / 100).toFixed(2);
const BIC_RE = /^[A-Z]{6}[A-Z2-9][A-NP-Z0-9]([A-Z0-9]{3})?$/;

/** Prüft einen Lauf vor dem Erzeugen der Datei; leer = in Ordnung. */
export function checkSepaTransfer(t: SepaTransfer): SepaProblem[] {
  const out: SepaProblem[] = [];
  if (!isValidIban(t.debtor.iban)) out.push({ code: "DEBTOR_IBAN", index: null });
  if (!sepaText(t.debtor.name, 70)) out.push({ code: "DEBTOR_NAME", index: null });
  if (!/^\d{4}-\d{2}-\d{2}$/.test(t.executionDate)) out.push({ code: "DATE", index: null });
  if (!t.messageId.trim() || t.messageId.length > 35) out.push({ code: "ID", index: null });
  if (t.payments.length === 0) out.push({ code: "NO_PAYMENTS", index: null });
  t.payments.forEach((p, i) => {
    if (!isValidIban(p.iban)) out.push({ code: "IBAN", index: i });
    if (!sepaText(p.name, 70)) out.push({ code: "NAME", index: i });
    if (!Number.isInteger(p.amountCents) || p.amountCents <= 0 || p.amountCents > 99_999_999_999) out.push({ code: "AMOUNT", index: i });
    if (p.bic && !BIC_RE.test(p.bic.replace(/\s+/g, "").toUpperCase())) out.push({ code: "BIC", index: i });
    if (!p.endToEndId.trim() || p.endToEndId.length > 35) out.push({ code: "ID", index: i });
  });
  return out;
}

/** SEPA-Überweisung pain.001.001.09 (eine Zahlungsinformation, Währung EUR). Wirft bei Fehlern, vorher checkSepaTransfer. */
export function buildSepaCreditTransfer(t: SepaTransfer): string {
  const problems = checkSepaTransfer(t);
  if (problems.length) throw new Error(`SEPA-Datei unvollständig: ${problems.map((p) => (p.index === null ? p.code : `${p.code}#${p.index + 1}`)).join(", ")}`);
  const total = t.payments.reduce((a, p) => a + p.amountCents, 0);
  const bic = (b: string | null | undefined) => {
    const v = (b ?? "").replace(/\s+/g, "").toUpperCase();
    return v ? `<FinInstnId><BICFI>${esc(v)}</BICFI></FinInstnId>` : "<FinInstnId><Othr><Id>NOTPROVIDED</Id></Othr></FinInstnId>";
  };
  const id = (s: string) => esc(sepaText(s, 35));
  const tx = t.payments.map((p) => [
    "<CdtTrfTxInf>",
    `<PmtId><EndToEndId>${id(p.endToEndId)}</EndToEndId></PmtId>`,
    `<Amt><InstdAmt Ccy="EUR">${amount(p.amountCents)}</InstdAmt></Amt>`,
    ...(p.bic ? [`<CdtrAgt>${bic(p.bic)}</CdtrAgt>`] : []),
    `<Cdtr><Nm>${esc(sepaText(p.name, 70))}</Nm></Cdtr>`,
    `<CdtrAcct><Id><IBAN>${esc(ibanOf(p.iban))}</IBAN></Id></CdtrAcct>`,
    `<RmtInf><Ustrd>${esc(sepaText(p.reference, 140))}</Ustrd></RmtInf>`,
    "</CdtTrfTxInf>",
  ].join(""));
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pain.001.001.09" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">',
    "<CstmrCdtTrfInitn>",
    `<GrpHdr><MsgId>${id(t.messageId)}</MsgId><CreDtTm>${esc(t.createdAt.slice(0, 19))}</CreDtTm><NbOfTxs>${t.payments.length}</NbOfTxs>`
      + `<CtrlSum>${amount(total)}</CtrlSum><InitgPty><Nm>${esc(sepaText(t.debtor.name, 70))}</Nm></InitgPty></GrpHdr>`,
    "<PmtInf>",
    `<PmtInfId>${id(t.messageId)}</PmtInfId><PmtMtd>TRF</PmtMtd><BtchBookg>${t.batchBooking === false ? "false" : "true"}</BtchBookg>`
      + `<NbOfTxs>${t.payments.length}</NbOfTxs><CtrlSum>${amount(total)}</CtrlSum>`
      + "<PmtTpInf><SvcLvl><Cd>SEPA</Cd></SvcLvl></PmtTpInf>"
      + `<ReqdExctnDt><Dt>${esc(t.executionDate)}</Dt></ReqdExctnDt>`
      + `<Dbtr><Nm>${esc(sepaText(t.debtor.name, 70))}</Nm></Dbtr>`
      + `<DbtrAcct><Id><IBAN>${esc(ibanOf(t.debtor.iban))}</IBAN></Id></DbtrAcct>`
      + `<DbtrAgt>${bic(t.debtor.bic)}</DbtrAgt><ChrgBr>SLEV</ChrgBr>`,
    ...tx,
    "</PmtInf>",
    "</CstmrCdtTrfInitn>",
    "</Document>",
  ].join("\n");
}

// ── Schweiz (GELD-14): Swiss Payment Standards, pain.001.001.09 ────────────────────────────────────────────────────

export interface SwissProblem {
  code: SepaProblem["code"] | "CURRENCY" | "DEBTOR_NOT_SWISS" | "NOT_SWISS" | "QR_IBAN";
  index: number | null;
}

/**
 * Prüft einen Lauf für die Schweizer Bankdatei: Inlandzahlung (Zahlungsart D der Swiss Payment Standards) in CHF oder EUR vom
 * Konto der Agentur in der Schweiz oder Liechtenstein an IBAN in der Schweiz oder Liechtenstein. QR-IBAN verlangen eine
 * QR-Referenz und taugen nicht für Auszahlungen; Konten im Ausland laufen über SEPA (EUR) oder die CSV-Datei.
 */
export function checkSwissTransfer(t: SepaTransfer, currency: string): SwissProblem[] {
  const out: SwissProblem[] = checkSepaTransfer(t);
  if (currency !== "CHF" && currency !== "EUR") out.push({ code: "CURRENCY", index: null });
  if (isValidIban(t.debtor.iban) && !isSwissIban(t.debtor.iban)) out.push({ code: "DEBTOR_NOT_SWISS", index: null });
  t.payments.forEach((p, i) => {
    if (!isValidIban(p.iban)) return;
    if (!isSwissIban(p.iban)) out.push({ code: "NOT_SWISS", index: i });
    else if (isQrIban(p.iban)) out.push({ code: "QR_IBAN", index: i });
  });
  return out;
}

/**
 * Überweisungsauftrag nach den Swiss Payment Standards (pain.001.001.09, Inlandzahlung): ohne SEPA-Dienstleistungsstufe und
 * ohne Spesenregel SLEV; Bank der Agentur mit BIC oder, ohne BIC, mit der Instituts-Identifikation (IID) aus der IBAN.
 * Wirft bei Fehlern, vorher checkSwissTransfer.
 */
export function buildSwissCreditTransfer(t: SepaTransfer, currency: "CHF" | "EUR"): string {
  const problems = checkSwissTransfer(t, currency);
  if (problems.length) throw new Error(`Bankdatei unvollständig: ${problems.map((p) => (p.index === null ? p.code : `${p.code}#${p.index + 1}`)).join(", ")}`);
  const total = t.payments.reduce((a, p) => a + p.amountCents, 0);
  const id = (s: string) => esc(sepaText(s, 35));
  const debtorIban = ibanOf(t.debtor.iban);
  const debtorBic = (t.debtor.bic ?? "").replace(/\s+/g, "").toUpperCase();
  const debtorAgent = debtorBic
    ? `<FinInstnId><BICFI>${esc(debtorBic)}</BICFI></FinInstnId>`
    : `<FinInstnId><ClrSysMmbId><ClrSysId><Cd>CHBCC</Cd></ClrSysId><MmbId>${esc(debtorIban.slice(4, 9))}</MmbId></ClrSysMmbId></FinInstnId>`;
  const tx = t.payments.map((p) => [
    "<CdtTrfTxInf>",
    `<PmtId><InstrId>${id(p.endToEndId)}</InstrId><EndToEndId>${id(p.endToEndId)}</EndToEndId></PmtId>`,
    `<Amt><InstdAmt Ccy="${currency}">${amount(p.amountCents)}</InstdAmt></Amt>`,
    `<Cdtr><Nm>${esc(sepaText(p.name, 70))}</Nm></Cdtr>`,
    `<CdtrAcct><Id><IBAN>${esc(ibanOf(p.iban))}</IBAN></Id></CdtrAcct>`,
    `<RmtInf><Ustrd>${esc(sepaText(p.reference, 140))}</Ustrd></RmtInf>`,
    "</CdtTrfTxInf>",
  ].join(""));
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<Document xmlns="urn:iso:std:iso:20022:tech:xsd:pain.001.001.09" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance" xsi:schemaLocation="urn:iso:std:iso:20022:tech:xsd:pain.001.001.09 pain.001.001.09.ch.03.xsd">',
    "<CstmrCdtTrfInitn>",
    `<GrpHdr><MsgId>${id(t.messageId)}</MsgId><CreDtTm>${esc(t.createdAt.slice(0, 19))}</CreDtTm><NbOfTxs>${t.payments.length}</NbOfTxs>`
      + `<CtrlSum>${amount(total)}</CtrlSum><InitgPty><Nm>${esc(sepaText(t.debtor.name, 70))}</Nm></InitgPty></GrpHdr>`,
    "<PmtInf>",
    `<PmtInfId>${id(t.messageId)}</PmtInfId><PmtMtd>TRF</PmtMtd><BtchBookg>${t.batchBooking === false ? "false" : "true"}</BtchBookg>`
      + `<NbOfTxs>${t.payments.length}</NbOfTxs><CtrlSum>${amount(total)}</CtrlSum>`
      + `<ReqdExctnDt><Dt>${esc(t.executionDate)}</Dt></ReqdExctnDt>`
      + `<Dbtr><Nm>${esc(sepaText(t.debtor.name, 70))}</Nm></Dbtr>`
      + `<DbtrAcct><Id><IBAN>${esc(debtorIban)}</IBAN></Id></DbtrAcct>`
      + `<DbtrAgt>${debtorAgent}</DbtrAgt>`,
    ...tx,
    "</PmtInf>",
    "</CstmrCdtTrfInitn>",
    "</Document>",
  ].join("\n");
}

export interface PayoutCsvRow {
  name: string;
  iban?: string | null;
  bic?: string | null;
  /** Auszahlungsweg, z. B. sepa, swift, paypal, wise */
  method: string;
  /** Adresse bei PayPal/Wise, Kontonummer bei SWIFT ohne IBAN */
  account?: string | null;
  /** Bankleitzahl/Routing (ABA, Transit, BSB, Sort Code) bei SWIFT (0186) */
  routingCode?: string | null;
  /** Bank und Bankland bei SEPA und SWIFT (0186) */
  bankName?: string | null;
  bankCountry?: string | null;
  amountCents: Cents;
  currency: string;
  reference: string;
}

/**
 * CSV (Semikolon, UTF-8 mit BOM, Dezimalkomma) für Banken ohne SEPA-Upload, Nicht-EUR-Auszahlungen und PayPal/Wise.
 * Die ersten sieben Spalten bleiben, wie sie waren; Bankleitzahl, Bank und Bankland stehen dahinter (0186), damit
 * vorhandene Vorlagen weiter passen. Bei SWIFT ohne IBAN steht die Kontonummer in „IBAN oder Konto“.
 */
export function buildPayoutCsv(rows: readonly PayoutCsvRow[]): string {
  const cell = (v: string) => (/[;"\n\r]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  // Name, Konto und Verwendungszweck stammen (teils) vom Creator: gegen Formeln schützen; der Betrag bleibt eine Zahl
  const text = (v: string) => cell(csvFormulaGuard(v));
  const head = ["Empfänger", "Auszahlungsweg", "IBAN oder Konto", "BIC", "Betrag", "Währung", "Verwendungszweck", "Bankleitzahl / Routing", "Bank", "Bankland"];
  const konto = (r: PayoutCsvRow) => {
    if (r.method === "paypal" || r.method === "wise") return r.account ?? "";
    const iban = ibanOf(r.iban ?? "");
    return iban || (r.method === "swift" ? (r.account ?? "").replace(/\s+/g, "") : "");
  };
  const bank = (r: PayoutCsvRow) => r.method === "sepa" || r.method === "swift";
  const lines = rows.map((r) => [
    text(r.name), text(r.method), text(konto(r)), text((r.bic ?? "").toUpperCase()),
    cell((r.amountCents / 100).toFixed(2).replace(".", ",")), text(r.currency), text(r.reference),
    text(r.method === "swift" ? (r.routingCode ?? "").replace(/\s+/g, "") : ""), text(bank(r) ? (r.bankName ?? "") : ""), text(bank(r) ? (r.bankCountry ?? "").toUpperCase() : ""),
  ].join(";"));
  return `﻿${[head.join(";"), ...lines].join("\r\n")}\r\n`;
}
